import React, { useState } from "react";
import { Snackbar, Alert } from "@mui/material";

// Snackbar context and custom hook
interface SnackbarContextType {
  showSnackbar: (
    message: string,
    autoHideDuration?: number,
    type?: "info" | "error" | "success"
  ) => void;
}

const SnackbarContext = React.createContext<SnackbarContextType | undefined>(
  undefined
);

export const useSnackbar = (): SnackbarContextType => {
  const context = React.useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

interface SnackbarProviderProps {
  children: React.ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
}) => {
  // Snackbar state and function
  const [snackbarType, setSnackbarType] = useState<
    "info" | "error" | "success"
  >("info");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarDuration, setSnackbarDuration] = useState(3000);

  const showSnackbar = (
    message: string,
    autoHideDurationMs: number = 3000,
    type: "info" | "error" | "success" = "info"
  ) => {
    setSnackbarMessage(message);
    setSnackbarDuration(autoHideDurationMs);
    setSnackbarType(type);
    setSnackbarOpen(true);
  };

  // nested providers don't conflict
  const context = React.useContext(SnackbarContext);
  if (context) {
    return children as any;
  }

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarDuration}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarType}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
